import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useFeatureFlags } from '../../contexts/FeatureFlagContext'

function deriveScreenKey(pathname) {
  const p = String(pathname || '').replace(/\/+$/, '')
  if (!p || p === '/') return null
  let cleaned = p.startsWith('/') ? p.slice(1) : p
  cleaned = cleaned
    .replace(/\/ecdd\/[^/]+$/i, '/ecdd/detail')
    .replace(/\/investigations\/(?!pipeline$)[^/]+$/i, '/investigations/case')
    .replace(/\/watch-list\/[^/]+$/i, '/watch-list')
  return `page:${cleaned}`
}

function isRulesScreen(pathname) {
  const p = String(pathname || '').toLowerCase()
  return p.includes('/monitor-safe/builder') || p.includes('/monitor-safe/rules')
}

function InlineMarkdown({ text }) {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*|`[^`]+`|_[^_\n]+_)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold text-slate-900">
          {part.slice(2, -2)}
        </strong>
      )
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={i} className="rounded bg-slate-100 px-1 py-0.5 text-[12px] text-teal-900">
          {part.slice(1, -1)}
        </code>
      )
    }
    if (part.startsWith('_') && part.endsWith('_') && part.length > 2) {
      return (
        <em key={i} className="text-slate-600 not-italic text-[12px]">
          {part.slice(1, -1)}
        </em>
      )
    }
    return <span key={i}>{part}</span>
  })
}

function ArchieHelpImage({ assetId, caption }) {
  const [src, setSrc] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    let objectUrl = null
    ;(async () => {
      try {
        const r = await authFetch(getLocalApiUrl(`/api/help/assets/${encodeURIComponent(assetId)}`))
        if (!r.ok) throw new Error('load failed')
        const blob = await r.blob()
        objectUrl = URL.createObjectURL(blob)
        if (!cancelled) setSrc(objectUrl)
      } catch {
        if (!cancelled) setFailed(true)
      }
    })()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [assetId])

  if (failed) {
    return <p className="text-[11px] text-slate-400 italic m-0">Screenshot unavailable</p>
  }
  if (!src) {
    return <div className="h-20 rounded-md bg-slate-100 animate-pulse" />
  }
  return (
    <figure className="my-1.5 space-y-1">
      <img
        src={src}
        alt={caption || 'Help screenshot'}
        className="rounded-md border border-slate-200 max-w-full h-auto max-h-44 object-contain bg-white"
      />
      {caption ? (
        <figcaption className="text-[10px] text-slate-500 leading-snug">{caption}</figcaption>
      ) : null}
    </figure>
  )
}

function parseAssetImage(line) {
  const m = String(line || '').trim().match(/^!\[([^\]]*)\]\(asset:([^)]+)\)$/i)
  if (!m) return null
  return { caption: m[1] || 'Screenshot', assetId: m[2].trim() }
}

/** Rich markdown-ish renderer for Archie's screen help answers. */
function renderAnswer(text) {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n')
  const nodes = []
  let i = 0
  let key = 0

  const flushList = (items, ordered) => {
    if (!items.length) return
    const Tag = ordered ? 'ol' : 'ul'
    nodes.push(
      <Tag
        key={key++}
        className={`my-1.5 pl-4 space-y-1 text-[13px] text-slate-700 ${
          ordered ? 'list-decimal' : 'list-disc'
        }`}
      >
        {items.map((item, idx) => (
          <li key={idx} className="leading-snug">
            <InlineMarkdown text={item} />
          </li>
        ))}
      </Tag>
    )
  }

  while (i < lines.length) {
    const raw = lines[i]
    const line = raw.trimEnd()
    const trimmed = line.trim()

    if (!trimmed) {
      i += 1
      continue
    }

    const img = parseAssetImage(trimmed)
    if (img) {
      nodes.push(
        <ArchieHelpImage key={key++} assetId={img.assetId} caption={img.caption} />
      )
      i += 1
      continue
    }

    if (trimmed.startsWith('#### ')) {
      nodes.push(
        <div key={key++} className="mt-2 mb-0.5 text-[12px] font-semibold text-teal-900">
          {trimmed.slice(5)}
        </div>
      )
      i += 1
      continue
    }
    if (trimmed.startsWith('### ')) {
      nodes.push(
        <div key={key++} className="mt-2.5 mb-1 text-[12px] font-bold uppercase tracking-wide text-slate-500">
          {trimmed.slice(4)}
        </div>
      )
      i += 1
      continue
    }
    if (trimmed.startsWith('## ')) {
      nodes.push(
        <div key={key++} className="text-[14px] font-semibold text-slate-900 mb-1">
          {trimmed.slice(3)}
        </div>
      )
      i += 1
      continue
    }
    if (trimmed.startsWith('# ')) {
      nodes.push(
        <div key={key++} className="text-[15px] font-semibold text-slate-900 mb-1">
          {trimmed.slice(2)}
        </div>
      )
      i += 1
      continue
    }

    if (trimmed.startsWith('> ')) {
      const quoteLines = []
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        quoteLines.push(lines[i].trim().replace(/^>\s?/, ''))
        i += 1
      }
      nodes.push(
        <div
          key={key++}
          className="my-1.5 rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-[12px] text-sky-900 leading-snug"
        >
          <InlineMarkdown text={quoteLines.join(' ')} />
        </div>
      )
      continue
    }

    if (/^[-*•]\s+/.test(trimmed)) {
      const items = []
      while (i < lines.length && /^[-*•]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*•]\s+/, ''))
        i += 1
      }
      flushList(items, false)
      continue
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items = []
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ''))
        i += 1
      }
      flushList(items, true)
      continue
    }

    // Paragraph: gather consecutive plain lines
    const para = []
    while (
      i < lines.length &&
      lines[i].trim() &&
      !lines[i].trim().startsWith('#') &&
      !lines[i].trim().startsWith('>') &&
      !/^[-*•]\s+/.test(lines[i].trim()) &&
      !/^\d+\.\s+/.test(lines[i].trim()) &&
      !parseAssetImage(lines[i].trim())
    ) {
      para.push(lines[i].trim())
      i += 1
    }
    nodes.push(
      <p key={key++} className="my-1 text-[13px] text-slate-700 leading-relaxed m-0">
        <InlineMarkdown text={para.join(' ')} />
      </p>
    )
  }

  return <div className="space-y-0.5">{nodes}</div>
}

export default function PsgAgentWidget() {
  const location = useLocation()
  const navigate = useNavigate()
  const { isModuleEnabled, loading: flagsLoading } = useFeatureFlags()
  const archieModuleOn = isModuleEnabled('archie')
  const [open, setOpen] = useState(false)
  const [status, setStatus] = useState(null)
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [creatingDraft, setCreatingDraft] = useState(false)
  const [loadingTopic, setLoadingTopic] = useState(false)
  const [messages, setMessages] = useState([])
  const [error, setError] = useState('')
  const bottomRef = useRef(null)

  const screenKey = useMemo(() => deriveScreenKey(location.pathname), [location.pathname])
  const onRulesScreen = isRulesScreen(location.pathname)

  useEffect(() => {
    if (flagsLoading || !archieModuleOn) {
      setStatus(null)
      return undefined
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await authFetch(getLocalApiUrl('/api/psgagent/status'))
        const j = await res.json()
        if (!cancelled && res.ok && j.success) setStatus(j.data)
      } catch {
        if (!cancelled) setStatus({ enabled: false })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [flagsLoading, archieModuleOn])

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, open])

  useEffect(() => {
    setMessages([])
    setError('')
  }, [screenKey])

  // Platform feature flag module:archie + server status (env kill-switch)
  if (flagsLoading || !archieModuleOn || !status?.enabled) return null

  const send = async (e) => {
    e?.preventDefault?.()
    const text = input.trim()
    if (!text || sending) return
    setInput('')
    setError('')
    const nextHistory = [...messages, { role: 'user', content: text }]
    setMessages(nextHistory)
    setSending(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/psgagent/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          screenKey,
          pathname: location.pathname,
          history: nextHistory.slice(0, -1).map((m) => ({ role: m.role, content: m.content })),
        }),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Agent request failed')
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: j.data.answer,
          citations: j.data.citations || [],
          mode: j.data.mode,
          ruleDraft: j.data.ruleDraft || null,
          ruleValidation: j.data.ruleValidation || null,
          canCreateDraft: Boolean(j.data.canCreateDraft),
          helpImages: j.data.helpImages || [],
          helpTopics: j.data.helpTopics || [],
        },
      ])
    } catch (err) {
      setError(err.message || 'Failed to get an answer')
    } finally {
      setSending(false)
    }
  }

  const openTopic = async (topic) => {
    if (!topic || loadingTopic || sending) return
    if (topic.action === 'open_help_panel') {
      window.dispatchEvent(new CustomEvent('psg:open-screen-help'))
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: topic.label },
        {
          role: 'assistant',
          content:
            'Use the **Help** button on this page for the full published guide. I’ve also signalled the help panel to open if this screen supports it.',
          helpTopics: [],
        },
      ])
      return
    }

    setLoadingTopic(true)
    setError('')
    setMessages((prev) => [...prev, { role: 'user', content: topic.label }])
    try {
      const res = await authFetch(getLocalApiUrl('/api/psgagent/help/topic'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topicId: topic.id,
          screenKey,
          pathname: location.pathname,
        }),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Could not load topic')
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: j.data.answer,
          helpTopics: j.data.helpTopics || [],
          helpImages: j.data.helpImages || [],
          mode: 'help_topic',
        },
      ])
    } catch (err) {
      setError(err.message || 'Failed to load topic')
    } finally {
      setLoadingTopic(false)
    }
  }

  const createDraftAndOpen = async (draft) => {
    if (!draft || creatingDraft) return
    setCreatingDraft(true)
    setError('')
    try {
      const res = await authFetch(getLocalApiUrl('/api/psgagent/rules/create-draft'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ draft }),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Could not create draft')
      const url = j.data.builderUrl || `/monitor-safe/builder/combined?id=${encodeURIComponent(j.data.ruleId)}`
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `Draft **${j.data.ruleId}** saved (disabled). Opening the Combined Rule Builder — review, then submit for approval when ready.`,
        },
      ])
      setOpen(false)
      navigate(url)
    } catch (err) {
      setError(err.message || 'Failed to create draft')
    } finally {
      setCreatingDraft(false)
    }
  }

  const suggestions = onRulesScreen
    ? [
        'Draft a rule: alert when CancelCredit > 1000',
        'Create a rule for high CashDrop vs Turnover',
        'What fields can I use in expressions?',
      ]
    : [
        'What can I do on this screen?',
        'Walk me through this workflow',
        'Draft a rule: alert when CancelCredit > 1000',
      ]

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[70] flex items-center gap-2 rounded-full bg-teal-700 hover:bg-teal-800 text-white shadow-lg px-4 py-3 text-sm font-semibold"
          title="Ask Archie"
        >
          <ChatBubbleLeftRightIcon className="h-5 w-5" />
          Ask Archie
        </button>
      )}

      {open && (
        <div className="fixed bottom-5 right-5 z-[70] w-[min(100vw-1.5rem,400px)] h-[min(72vh,600px)] flex flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="flex items-start justify-between gap-2 px-4 py-3 bg-teal-800 text-white">
            <div className="min-w-0">
              <div className="text-sm font-semibold">Archie</div>
              <div className="text-[11px] text-teal-100 truncate mt-0.5">
                {onRulesScreen ? 'Rules co-pilot' : `Help for ${screenKey || 'this page'}`}
                {!status.llmConfigured ? ' · excerpts mode' : ''}
              </div>
            </div>
            <button type="button" className="text-teal-100 hover:text-white" onClick={() => setOpen(false)}>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-slate-50">
            {messages.length === 0 && (
              <div className="text-xs text-slate-600 space-y-2">
                <p className="m-0">
                  {onRulesScreen
                    ? 'Describe a monitoring rule in plain English. I will draft it for you to review in the Combined builder — you still submit and publish.'
                    : 'Ask about this screen — I’ll give a short summary, then options you can tap to explore each topic.'}
                </p>
                <div className="flex flex-col gap-1.5">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="text-left rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-teal-900 hover:border-teal-300"
                      onClick={() => {
                        setInput(s)
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, idx) => (
              <div
                key={idx}
                className={`rounded-xl px-3 py-2.5 text-[13px] ${
                  m.role === 'user'
                    ? 'bg-teal-700 text-white ml-8 whitespace-pre-wrap'
                    : 'bg-white border border-slate-200 text-slate-800 mr-3'
                }`}
              >
                {m.role === 'assistant' ? renderAnswer(m.content) : m.content}
                {m.role === 'assistant' && Array.isArray(m.helpTopics) && m.helpTopics.length > 0 && (
                  <div className="mt-2.5 pt-2 border-t border-slate-100 space-y-1.5">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      Explore a topic
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {m.helpTopics.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          disabled={loadingTopic || sending}
                          onClick={() => openTopic(t)}
                          className="text-left rounded-lg border border-teal-200 bg-teal-50/60 hover:bg-teal-50 hover:border-teal-400 disabled:opacity-50 px-2.5 py-2"
                        >
                          <div className="text-[12px] font-semibold text-teal-900">{t.label}</div>
                          {t.teaser ? (
                            <div className="text-[11px] text-slate-600 mt-0.5 leading-snug line-clamp-2">
                              {t.teaser}
                            </div>
                          ) : null}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {m.role === 'assistant' && m.ruleDraft && (
                  <div className="mt-2 pt-2 border-t border-slate-100 space-y-2">
                    <div className="text-[11px] text-slate-600 font-medium">
                      {m.ruleDraft.name} · {m.ruleDraft.severity}
                    </div>
                    <ul className="m-0 pl-4 text-[11px] text-slate-700 list-disc">
                      {(m.ruleDraft.expressions || []).map((ex, i) => (
                        <li key={i}>
                          <code className="text-teal-900">{ex}</code>
                        </li>
                      ))}
                    </ul>
                    {m.canCreateDraft && (
                      <button
                        type="button"
                        disabled={creatingDraft}
                        onClick={() => createDraftAndOpen(m.ruleDraft)}
                        className="w-full rounded-lg bg-teal-700 hover:bg-teal-800 disabled:opacity-50 text-white text-xs font-semibold px-3 py-2"
                      >
                        {creatingDraft ? 'Creating…' : 'Create draft & open builder'}
                      </button>
                    )}
                    {!m.canCreateDraft && m.ruleValidation?.errors?.length > 0 && (
                      <div className="text-[11px] text-amber-800 bg-amber-50 rounded-md px-2 py-1.5">
                        Fix issues before saving: {m.ruleValidation.errors.join('; ')}
                      </div>
                    )}
                  </div>
                )}
                {m.role === 'assistant' && Array.isArray(m.citations) && m.citations.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-100 flex flex-wrap gap-1">
                    {m.citations.slice(0, 4).map((c, i) => (
                      <span
                        key={i}
                        className="inline-flex rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[10px] font-medium"
                      >
                        {c.title || c.screen_key}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {sending && (
              <div className="text-xs text-slate-500 px-1">Thinking…</div>
            )}
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-xs px-2.5 py-2">
                {error}
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={send} className="border-t border-slate-200 p-2.5 bg-white flex gap-2">
            <input
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20"
              placeholder={onRulesScreen ? 'Describe a rule to draft…' : 'Ask about this screen…'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={sending}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="rounded-lg bg-teal-700 hover:bg-teal-800 disabled:opacity-50 text-white px-3"
              aria-label="Send"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  )
}
